/* eslint-disable @typescript-eslint/no-explicit-any */
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import authReducer from "./slices/authSlice";
import giftTableReducer, {
  mergeGiftTableState,
  GiftTableState as OwnerGiftTableState,
} from "./slices/giftTableSlice";
import guestGiftTableReducer, {
  createBaseGuestsGiftTableState,
  GiftTableState as GuestGiftTableState,
} from "./slices/guestsGiftTableSlice";
import registrationReducer, {
  createInitialRegistrationState,
  RegistrationState,
} from "./slices/registrationSlice";
import animationReducer from "./slices/animationSlice";
import guestCartReducer, {
  createGuestCartInitialState,
  GuestCartState,
} from "./slices/guestCartSlice";
import checkoutReducer, {
  createCheckoutInitialState,
  CheckoutState,
} from "./slices/checkoutSlice";
import eventBusReducer from "./slices/eventBusSlice";
import pricingReducer, {
  createInitialPurchaseState,
  createInitialDonationState,
  PurchaseState,
  DonationState,
} from "./slices/pricingSlice";
import withdrawalsReducer, {
  createInitialWithdrawalsState,
  WithdrawalsState,
} from "./slices/withdrawalsSlice";
import uiReducer from "./slices/uiSlice";

const appReducer = combineReducers({
  auth: authReducer,
  giftTable: giftTableReducer,
  guestGiftTable: guestGiftTableReducer,
  registration: registrationReducer,
  animation: animationReducer,
  guestCart: guestCartReducer,
  checkout: checkoutReducer,
  eventBus: eventBusReducer,
  pricing: pricingReducer,
  withdrawals: withdrawalsReducer,
  uiState: uiReducer,
});

// Define a root reducer that handles a reset action
const rootReducer = (state: any, action: any) => {
  if (action.type === "app/reset") {
    state = undefined;
  }
  return appReducer(state, action);
};

const safeParse = <T>(key: string): T | null => {
  try {
    const value = localStorage.getItem(key);
    if (!value) {
      return null;
    }
    return JSON.parse(value) as T;
  } catch (error) {
    console.error(`Failed to parse localStorage key '${key}'`, error);
    return null;
  }
};

const loadPreloadedState = () => {
  const preloaded: any = {};

  const savedUser = safeParse("userState");
  if (savedUser) {
    preloaded.auth = { isAuthenticated: true, user: savedUser };
  }

  const savedGiftTable = safeParse<Partial<OwnerGiftTableState>>(
    "giftTableState"
  );
  if (savedGiftTable) {
    preloaded.giftTable = mergeGiftTableState(savedGiftTable);
  }

  const savedGuestsGiftTable = safeParse<Partial<GuestGiftTableState>>(
    "guestsGiftTableState"
  );
  if (savedGuestsGiftTable) {
    const baseGuestsState = createBaseGuestsGiftTableState();
    preloaded.guestGiftTable = {
      ...baseGuestsState,
      ...savedGuestsGiftTable,
      eventLocation: {
        ...baseGuestsState.eventLocation,
        ...(savedGuestsGiftTable.eventLocation ?? {}),
      },
      products: savedGuestsGiftTable.products ?? [],
      loading: false,
    };
  }

  const savedCheckout = safeParse<CheckoutState>("checkoutState");
  if (savedCheckout) {
    preloaded.checkout = {
      ...createCheckoutInitialState(),
      ...savedCheckout,
    };
  }

  const savedCart = safeParse<GuestCartState["items"]>("guestCart");
  if (Array.isArray(savedCart)) {
    preloaded.guestCart = {
      ...createGuestCartInitialState(),
      items: savedCart,
    };
  }

  const savedPurchaseState = safeParse<Partial<PurchaseState>>("purchaseState");
  const savedDonationState = safeParse<Partial<DonationState>>("donationState");
  if (savedPurchaseState || savedDonationState) {
    preloaded.pricing = {
      purchaseState: {
        ...createInitialPurchaseState(),
        ...(savedPurchaseState ?? {}),
      },
      donationState: {
        ...createInitialDonationState(),
        ...(savedDonationState ?? {}),
      },
    };
  }

  const savedWithdrawals = safeParse<Partial<WithdrawalsState>>(
    "withdrawalsState"
  );
  if (savedWithdrawals) {
    preloaded.withdrawals = {
      ...createInitialWithdrawalsState(),
      ...savedWithdrawals,
    };
  }

  const savedRegistration = safeParse<Partial<RegistrationState>>(
    "registrationState"
  );
  if (savedRegistration) {
    preloaded.registration = {
      ...createInitialRegistrationState(),
      ...savedRegistration,
    };
  }

  return preloaded;
};

const store = configureStore({
  reducer: rootReducer,
  preloadedState: loadPreloadedState(),
});

export default store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const resetApp = () => ({
  type: "app/reset",
});
